/**
 * Fetches overall health across all 7 composite alarms via the BFF.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { AtmsApiClient } from '@amzn/et-console-components';
import { AlarmHistoryHookState, CategoryAlarmHistoryResponse, toFetchErrorKind } from '../types';
import { REFRESH_INTERVAL_MS } from '../components/constants';

export function useOverallAlarmHistory(days = 30): AlarmHistoryHookState {
  const [state, setState] = useState<AlarmHistoryHookState>({
    isLoading: true,
    data: null,
    error: null,
    lastUpdated: null,
  });
  const requestIdRef = useRef(0);

  const fetchData = useCallback(() => {
    const requestId = ++requestIdRef.current;

    if (!Number.isFinite(days) || days < 1) {
      console.error(`useOverallAlarmHistory: days must be a positive integer, got: ${days}`);
      setState({ isLoading: false, data: null, error: 'unknown', lastUpdated: null });
      return;
    }

    setState(prev => ({ ...prev, isLoading: prev.data === null, error: null }));

    AtmsApiClient.httpGet(`/api/observability/overall-alarm-history?days=${days}`)
      .then((result: CategoryAlarmHistoryResponse) => {
        if (requestId === requestIdRef.current) {
          setState({ isLoading: false, data: result, error: null, lastUpdated: new Date() });
        }
      })
      .catch((err: { status?: number; response?: { status?: number } }) => {
        if (requestId !== requestIdRef.current) return;
        setState(prev => ({ ...prev, isLoading: false, error: toFetchErrorKind(err) }));
      });
  }, [days]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchData]);

  return state;
}
